/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState, useEffect, ChangeEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Notification from '../../ChoreoSystem/Notification/Notification';

import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Container,
  Divider,
  Select,
  MenuItem,
  Tooltip,
  Zoom,
  IconButton,
  Button,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import { CloudDownloadRounded, HelpTwoTone, Warning, Add, Info } from '@material-ui/icons';
import SecurityHeader from './SecurityHeader';
import { useStyles } from './style';
import SwaggerUI from './SwaggerUI';
import PasswordCopyField from '../../../elements/PasswordCopyField';
import {
  ApiReducer,
  ApplicationKeyReducer,
  ApplicationState,
  EndpointInfo,
  EnvConfigReducer,
  GraphqlReducer,
  IDPReducer,
  Key,
  KeyType,
  OrganizationMetadataReducer,
  Subscription,
  SubscriptionsReducer,
  SwaggerReducer,
  TestKeyType,
  UserReducer,
} from '../../../data/interfaces';
import {
  isValidData,
  getApiGatewayUrl,
  getIsInternalAPI,
  getAllApiGatewayUrls,
  isProductionKeyGenerationAccessible,
  isSandboxKeyGenerationAccessible,
  isCredentialMenuAccessible,
  isThirdPartyApi,
  getEndpointUrls,
  getAPIType,
  getVisibilityFromAccessibilityType,
} from "../../../data/utils";
import {
  createAppKeysAndToken,
  fetchAppKeys,
  fetchSubscriptions,
  fetchAPIVersionRangeSubscriptions,
  generateApplicationToken,
  fetchGraphqlResponse,
  fetchIDPs,
  fetchSwagger,
  fetchSwaggerUnauthenticated,
  generateApiTestKey,
} from '../../../data/operations';
import { setErrorNotification } from '../../../store/actions/notification';
import ApiInfoBar from '../../shared/ApiInfoBar';
import OutlineButton from '../../../elements/OutlineButton';
import {
  ASGARDEO_KEY_MANAGER_TYPE,
  DEFAULT_APPLICATION_LIMIT,
  DEFAULT_APP_INTERNAL_PREFIX,
  DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX,
  PATH_SEPARATOR,
  SUBSCRIBE_SCOPE,
  API_TYPE_HTTP,
  API_TYPE_WS,
  API_TYPE_GRAPHQL,

} from '../../../data/constants';
import { PreLoaderLogo } from '../../../elements/PreLoaderLogo';
import { createOauthTokenSuccess } from '../../../store/actions/applicationKeys';
import CustomIcons from '../../../icons/CustomIcons';
import { OpenAPIObject } from 'openapi3-ts';
import Settings from '../../../conf/settings';
import {
  trackTryAPIPageLoad,
  trackTryOutGoToCredentialsClick,
  trackTryOutSwaggerDownload
} from "../../../util/app-insights";
import WarningBanner from '../../shared/WarningBanner';
import DarkTooltip from '../../../elements/DarkTooltip';
import GraphiqlViewer from './GraphiqlViewer';
import Badge from '../../../elements/Badge';
import { BadgeSize, BadgeVariant } from '../../../elements/Badge/Badge';
import { getScopeName } from '../../../util/token-generation';
import InfoTooltip from '../../../elements/CustomTooltip/InfoTooltip';
import WebSocketViewer from './WebSocketViewer';
import CustomAutoComplete from '../../../elements/CustomAutoComplete/CustomAutoComplete';
import { SecuritySchemeType } from './utils';

const PROD_ENV_LABEL = 'Production';

interface ApiTryOutProps {
  dispatchGenerateAppKeyAndToken: (
    applicationId: string,
    organizationId: string,
    grantTypes: string[],
    callbackUrl: string,
    scopes: string[],
    additionalProperties?: Record<string, unknown>,
    keyType?: KeyType,
    keyManager?: string
  ) => void;
  dispatchGenerateApplicationToken: (
    applicationId: string,
    selectedScopes: string[],
    key: Key
  ) => void;
  dispatchFetchGraphqlResponse: () => void;
  dispatchFetchSubscriptions: (
    apiId: string,
    organizationId: string,
    type: string,
    limit?: number
  ) => void;
  dispatchFetchVersionRangeSubscriptions: (
    apiId: string,
    organizationId: string
  ) => void;
  dispatchFetchAppKeys: (appId: string, organizationId: string) => void;
  dispatchGenerateApiTestKey: (apiId: string, environment: string) => void;
  dispatchFetchIDPs: (organizationId: string) => void;
  dispatchFetchSwagger: (
    apiId: string,
    organizationId: string,
    environmentName?: string,
    apiType?: string
  ) => void;
  dispatchFetchSwaggerUnauthenticated: (
    apiId: string,
    organizationId: string,
    environmentName?: string,
    apiType?: string
  ) => void;
  apiInfo: ApiReducer;
  keysInfo: ApplicationKeyReducer;
  subscriptionInfo: SubscriptionsReducer;
  userInfo: UserReducer;
  swaggerInfo: SwaggerReducer;
  graphqlResponse: GraphqlReducer;
  envConfigReducer: EnvConfigReducer;
  idpInfo: IDPReducer;
  organizationMetadataReducer: OrganizationMetadataReducer;
}

function ApiTryOut(props: ApiTryOutProps) {
  const {
    dispatchGenerateApplicationToken,
    dispatchGenerateAppKeyAndToken,
    dispatchFetchSubscriptions,
    dispatchFetchVersionRangeSubscriptions,
    dispatchFetchAppKeys,
    dispatchGenerateApiTestKey,
    dispatchFetchIDPs,
    dispatchFetchSwagger,
    dispatchFetchSwaggerUnauthenticated,
    apiInfo,
    keysInfo,
    subscriptionInfo,
    userInfo,
    swaggerInfo,
    graphqlResponse,
    idpInfo,
    organizationMetadataReducer,
    dispatchFetchGraphqlResponse,
  } = props;
  const { api } = apiInfo;
  const apiGatewayUrl = api?.context
    ? getApiGatewayUrl(api?.context, userInfo.selectedOrg.name)
    : '';
  const selectedOrgHandleName = userInfo.selectedOrg.handle;
  const isApimTokenObtained = userInfo.apimTokenObtained;
  const allowedScopes = userInfo?.user?.allowedScopes || "";
  const { swagger, loading: swaggerLoading } = swaggerInfo;
  const { graphqlInfo, loading: isGraphqlResponseFetching } = graphqlResponse;
  const { subscriptions, isLoading: isSubscriptionFetching } = subscriptionInfo;
  const isKeysFetching = keysInfo.isLoading;
  const apiKey = keysInfo?.apiKey.apikey;
  const idpList = idpInfo?.idps.list;
  const defaultAsgardeoKeyManagerName =
    DEFAULT_ASGARDEO_KEY_MANAGER_PREFIX + selectedOrgHandleName;
  const isResidentKeyManagerNotUsed = !keysInfo?.keys?.list.find(
    (key) => key.keyManager === Settings.keyManager
  );
  const isAsgardeoKeyManagerAvailable =
    idpList.filter(
      (idp) =>
        idp.type === ASGARDEO_KEY_MANAGER_TYPE &&
        idp.name === defaultAsgardeoKeyManagerName
    ).length > 0;

    enum TokenType {
      TEST_KEY = 'test-key',
      APPLICATION = 'application',
      API_KEY = 'apikey',
    }

  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedApplicationName, setSelectedApplicationName] = useState('');
  const [isAsgardeoManagedApplication, setIsAsgardeoManagedApplication] = useState(false);
  const [testTokenType, setTestTokenType] = useState(Settings.apiTryoutTestKeyEnabled ? TokenType.TEST_KEY : TokenType.APPLICATION);
  const [scopes, setScopes] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCritivalEnv, setIsCritivalEnv] = useState(false);
  const [swaggerObj, setSwaggerObj] = useState(swagger);
  const [endpointUrlList, setEndpointUrlList] = useState<EndpointInfo[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>("");
  const [selectedGatewayEnvironment, setSelectedGatewayEnvironment] = useState<string>("");
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");
  const [selectedVisibility, setSelectedVisibility] = useState<string>("");
  const { enableInV2 } = Settings;
  const [copiedTooltipVisible, setCopiedTooltipVisible] = useState(false);
  const enableCustomDomainView = enableInV2 && isApimTokenObtained;
  const isAuthorizedUserToSubscribe = allowedScopes?.includes(SUBSCRIBE_SCOPE);

  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const getAuthorizationHeader = () => {
    if (testTokenType === TokenType.TEST_KEY) {
      return 'test-key';
    }
    if (testTokenType === TokenType.APPLICATION) {
      return api?.authorizationHeader || 'Authorization';
    }
    if (testTokenType === TokenType.API_KEY) {
      return api?.apiKeyHeader || 'api-key';
    }
    return '';
  }
  const [authorizationHeader, setAuthorizationHeader] = useState(getAuthorizationHeader());

  const defaultAppName =
    DEFAULT_APP_INTERNAL_PREFIX + userInfo?.userInfo?.sub + '_' + api?.name;
  const [securitySchemeType, setSecuritySchemeType] = useState(SecuritySchemeType.OAUTH);
  const fileName = 'swagger.json';
  const isDefaultCredentialsAvailable = !!subscriptions?.list?.find(
    (subscription) => subscription.applicationInfo?.name === defaultAppName
  );

  const endpointUrl = api?.advertiseInfo?.apiExternalProductionEndpoint || '';
  const sandboxUrl = api?.advertiseInfo?.apiExternalSandboxEndpoint || '';
  const [headerName, setHeaderName] = useState('Authorization');
  const [headerValue, setHeaderValue] = useState('');
  const [openSecurityHeader, setOpenSecurityHeader] = useState(false);
  const applicationList = subscriptions.list.map(subscription => {
    if (subscription.applicationInfo?.name === defaultAppName) {
      return 'Default Credentials'
    }
    return subscription.applicationInfo?.name
  })
  const getUpdatedSwagger = (swagger: OpenAPIObject, forDownload?: boolean): OpenAPIObject => {
    let newSwaggerObj: OpenAPIObject = {} as OpenAPIObject;
    if (swagger && Object.keys(swagger).length > 0 && apiGatewayUrl) {
      newSwaggerObj = JSON.parse(JSON.stringify(swagger));
      // Checking if OpenAPI spec is 2.0 or 3.0
      if (newSwaggerObj.swagger?.startsWith('2')) {
        newSwaggerObj = {
          ...newSwaggerObj,
          host: selectedEndpoint,
          basePath: apiGatewayUrl.slice(-1) === '/' ? '' : '/',
        };
      } else if (newSwaggerObj.openapi?.startsWith('3')) {
        if (forDownload) {
          return {
            ...newSwaggerObj,
            servers: endpointUrlList.map((endpoint) => ({ url: endpoint.url })),
          };
        }
        {
          return {
            ...newSwaggerObj,
            servers: [{ url: selectedEndpoint }],
          };
        }
      }
    }
    return newSwaggerObj;
  };

  const [isApiKeySupportedApi, setIsApiKeySupportedApi] = useState(false);
  const [isOAuthSupportedApi, setIsOAuthSupportedApi] = useState(false);

  useEffect(() => {
    if (api) {
      setIsApiKeySupportedApi(api?.securityScheme?.includes('api_key'));
      setIsOAuthSupportedApi(api?.securityScheme?.includes('oauth2'));
    }
  }, [api]);

  const getUpdatedAsyncAPI = (asyncapi: any) => {
    if (asyncapi && Object.keys(asyncapi).length > 0 && apiGatewayUrl) {
      let newAsyncapi = JSON.parse(JSON.stringify(asyncapi));
      if (newAsyncapi.swagger?.startsWith('2')) {
        newAsyncapi = {
          ...newAsyncapi,
          host: apiGatewayUrl.substring(8),
          basePath: apiGatewayUrl.slice(-1) === '/' ? '' : '/',
        };
      } else if (newAsyncapi.asyncapi?.startsWith('3')) {
        newAsyncapi = { ...newAsyncapi, servers: [{ url: apiGatewayUrl }] };
      }
      return newAsyncapi;
    }
    return null;
  }

  const getDownloadLink = (): string => {
    const updatedSwagger = getUpdatedSwagger(swaggerObj, true);
    const downloadSwagger = JSON.stringify({ ...updatedSwagger });
    return (
      "data:text/json;charset=utf-8, " + encodeURIComponent(downloadSwagger)
    );
  };

  useEffect(() => {
    dispatchFetchIDPs(userInfo?.selectedOrg?.uuid);
  }, []);

  const getTestViewForApi = (api: Api) => {
    switch (getAPIType(api)) {
      case API_TYPE_HTTP:
        return (
          <Grid item xs={12} className={classes.swaggerUI}>
            <SwaggerUI
              spec={
                isThirdPartyApi(api)
                  ? swaggerObj
                  : getUpdatedSwagger(swaggerObj)
              }
              securitySchemeType={securitySchemeType}
              accessTokenProvider={isThirdPartyApi(api) ? headerValue : token}
              api={api}
              authorizationHeader={
                isThirdPartyApi(api) ? headerName : authorizationHeader
              }
              apiGatewayUrl={enableInV2 ? selectedEndpoint : apiGatewayUrl}
            />
          </Grid>
        );
      case API_TYPE_GRAPHQL:
        return (
          <GraphiqlViewer
            token={token}
            authorizationHeader={authorizationHeader}
            apiGatewayUrl={
              enableCustomDomainView ? selectedEndpoint : apiGatewayUrl
            }
            securitySchemeType={securitySchemeType}
          />
        );
      case API_TYPE_WS:
        return (
          <WebSocketViewer
            asyncapi={isThirdPartyApi(api)
              ? swaggerObj
              : getUpdatedAsyncAPI(swaggerObj)} 
            token={token}
            authorizationHeader={authorizationHeader}
            apiGatewayUrl={
              enableCustomDomainView ? selectedEndpoint : apiGatewayUrl
            }
            securitySchemeType={securitySchemeType}
            
          />);
      default:
        return (
          <Grid item xs={12} className={classes.swaggerUI}>
            <SwaggerUI
              spec={
                isThirdPartyApi(api)
                  ? swaggerObj
                  : getUpdatedSwagger(swaggerObj)
              }
              securitySchemeType={securitySchemeType}
              accessTokenProvider={isThirdPartyApi(api) ? headerValue : token}
              api={api}
              authorizationHeader={
                isThirdPartyApi(api) ? headerName : authorizationHeader
              }
              apiGatewayUrl={enableInV2 ? selectedEndpoint : apiGatewayUrl}
            />
          </Grid>
        );
    }
  };

  useEffect(() => {
    trackTryAPIPageLoad();
    if (applicationList.length > 0) {
      const firstApp = applicationList[0] as string;
      setSelectedApplicationName(firstApp);
      fetchAppKeys(getIdFromName(firstApp) as string);
    }
  }, []);

  useEffect(() => {
    if (
      applicationList.length > 0 &&
      token === '' && keysInfo?.keys?.count > 0 &&
      keysInfo.oauthToken.accessToken === '' && selectedApplicationId === '') {
      setSelectedApplicationId(getIdFromName(applicationList[0] as string) as string)
    } else if (applicationList.length > 0 && keysInfo.oauthToken.accessToken === '' && keysInfo?.keys?.count === 0 && token === '') {
      setSelectedApplicationId(getIdFromName(applicationList[0] as string) as string);
    }
  }, [keysInfo?.keys?.list]);

  useEffect(() => {
    if (enableCustomDomainView && !!api.id) {
      dispatchFetchGraphqlResponse();
    }
  }, [api]);

  useEffect(() => {
    if (isValidData(api) && userInfo.orgs.length > 0) {
      dispatch(
        createOauthTokenSuccess({
          accessToken: '',
          tokenScopes: [],
          validityTime: 0,
        })
      );
      if (isAuthorizedUserToSubscribe) {
        if (api.version.startsWith("v") && Settings.intelligentRoutingFeatureEnabled) {
          dispatchFetchVersionRangeSubscriptions(api.id, userInfo.selectedOrg.uuid);
        } else {
          dispatchFetchSubscriptions(api.id, userInfo.selectedOrg.uuid, 'api' ,DEFAULT_APPLICATION_LIMIT);
          // This is not the ideal fix for the issue.Just a workaround.
          // To properly handle this , it shoudl have a paginated dropdown.
        }
      }
      if (api.scopes?.length > 0) {
        setScopes(api.scopes.map(scope => scope.key));
        setSelectedScopes(api.scopes.map(scope => scope.key));
      }
    }
  }, [api]);

  useEffect(() => {
    setHeaderName(apiInfo.api.authorizationHeader);
    setHeaderValue(apiInfo.headers.headerValue);
  }, [apiInfo]);

  useEffect(() => {
    if (keysInfo?.oauthToken) {
      setToken(keysInfo.oauthToken.accessToken);
    }
  }, [keysInfo?.oauthToken]);

  useEffect(() => {
    if (apiKey) {
      setToken(apiKey);
    }
  }, [apiKey]);

  useEffect(() => {
    const apiEndpointUrls = api ? getEndpointUrls(api) : [];
    if (enableCustomDomainView && graphqlInfo && apiEndpointUrls?.length > 0) {
      const defaultUrls = getAllApiGatewayUrls(
        apiEndpointUrls,
        graphqlInfo,
        (api.versionInfo && api.versionInfo?.majorRange.isLatest) || false,
        api.version,
        false,
        userInfo.selectedOrg
      );
      const endpointUrls: EndpointInfo[] = [...defaultUrls];
      if (isThirdPartyApi(api)) {
        endpointUrls.push({
          url: endpointUrl,
          environment: "Backend",
          environmentDisplayName: "Backend",
          choreoEnvironmentName: "Backend",
          accessibilityType: "",
          isSandboxEndpoint: false,
          isCritical: true,
          isSandbox:false,
        });
        if (sandboxUrl) {
          endpointUrls.push({
            url: sandboxUrl,
            environment: "Sandbox",
            environmentDisplayName: "Sandbox",
            choreoEnvironmentName: "Sandbox",
            accessibilityType: "",
            isSandboxEndpoint: true,
            isCritical: false,
            isSandbox:true,
          });
        }
      }
      const prodEnv = endpointUrls.find(
        (endpoint) => endpoint.environmentDisplayName === PROD_ENV_LABEL
      );
      const selectedEnv = prodEnv || endpointUrls[0];
      const defaultEndpoint = endpointUrls.find(
        (endpoint) =>
          endpoint.environmentDisplayName === selectedEnv.environmentDisplayName
      );
      setEndpointUrlList(endpointUrls);
      if (defaultEndpoint?.isCritical) {
        setCurrentKeyType(defaultEndpoint?.isSandbox ? KeyType.SANDBOX : KeyType.PRODUCTION);
        setIsCritivalEnv(true);
      } else {
        setCurrentKeyType(KeyType.SANDBOX);
        setIsCritivalEnv(false);
      }
      setSelectedEnvironment(selectedEnv?.choreoEnvironmentName);
      setSelectedGatewayEnvironment(selectedEnv?.environment || "");
      setSelectedVisibility(
        getVisibilityFromAccessibilityType(selectedEnv?.accessibilityType)
      );
      setSelectedEndpoint(defaultEndpoint?.url || "");
    }
  }, [graphqlInfo, api]);

  useEffect(() => {
    const newSwaggerObj: OpenAPIObject = { ...swagger };
    setSwaggerObj(newSwaggerObj);
  }, [swagger]);

  // Load the definition of the revision deployed to the selected gateway environment, since the
  // resources can differ between environments. Third-party (advertised) APIs are not deployed to
  // a gateway environment, so their definition is not environment specific.
  useEffect(() => {
    if (!selectedGatewayEnvironment || !api?.id || isThirdPartyApi(api)) {
      return;
    }
    if (isApimTokenObtained) {
      dispatchFetchSwagger(
        api.id,
        userInfo.selectedOrg.uuid,
        selectedGatewayEnvironment,
        getAPIType(api)
      );
    } else {
      dispatchFetchSwaggerUnauthenticated(
        api.id,
        organizationMetadataReducer.organizationMetadata.uuid,
        selectedGatewayEnvironment,
        getAPIType(api)
      );
    }
  }, [selectedGatewayEnvironment, api?.id]);

  const [currentKeyType, setCurrentKeyType] = useState<KeyType>(
    KeyType.PRODUCTION
  );

  useEffect(() => {
    if (applicationList?.length > 0 && selectedApplicationId === '') {
      const applicationName = applicationList[0] as string;
      setSelectedApplicationName(applicationName);
      const applicationId = getIdFromName(applicationName) as string;
      setSelectedApplicationId(applicationId);
      if (selectedApplicationId === '') {
        fetchAppKeys(applicationId);
      }
    }
  }, [applicationList]);

  /**
   * Generates access token for a given app
   */
  const generateAccessToken = async (applicationId: string, selectedPermissions: string[]) => {
    setIsUpdating(true);
    const grantTypes: string[] = ['refresh_token', 'client_credentials'];
    try {
      const envKey = keysInfo.keys.list.find(key=> key.keyType === currentKeyType);
      let keyManager = Settings.keyManager;
      if (isResidentKeyManagerNotUsed && isAsgardeoKeyManagerAvailable) {
        keyManager = defaultAsgardeoKeyManagerName;
      }
      if (envKey && applicationId) {
        await dispatchGenerateApplicationToken(
          applicationId as string,
          selectedPermissions,
          envKey
        );
      } else if (applicationId) {
        await dispatchGenerateAppKeyAndToken(
          applicationId,
          userInfo.selectedOrg.uuid,
          grantTypes,
          '',
          selectedPermissions,
          {},
          currentKeyType,
          keyManager
        );
      }
      setIsUpdating(false);
    } catch (error) {
      setIsUpdating(false);
      dispatch(
        setErrorNotification(
          'ERROR: ' + error.response.data.description ||
            error.response.data.error_description
        )
      );
    }
  };

  /**
   * Handle onChange of inputs
   * @param {*} event event
   * @memberof ApiTryOut
   */
  function handleChanges(event: any) {
    const { target } = event;
    const { name, value } = target;
    switch (name) {
      case 'accessToken':
        setToken(value);
        break;
      default:
    }
  }

  const fetchAppKeys = async (applicationId: string) => {
    await dispatchFetchAppKeys(applicationId, userInfo.selectedOrg.uuid);
  }

  const fetchAccessToken = async (applicationId: string, selectedPermissions: string[]) => {
    await generateAccessToken(applicationId, selectedPermissions);
  };

  const onTryoutApplicationChange = (event: ChangeEvent<any>) => {
    const applicationName = event.target.value;
    const applicationId = getIdFromName(applicationName) as string;
    fetchAppKeys(applicationId);

    const asgardeoAppId = subscriptions.list.find((subscription: Subscription) => {
      return subscription.applicationInfo?.name === applicationName
    })?.applicationInfo?.attributes?.asgardeo_app_id || '';
    setIsAsgardeoManagedApplication(asgardeoAppId !== '');
    setSelectedScopes(scopes);
    setToken('');
    setSelectedApplicationId(applicationId);
    setSelectedApplicationName(applicationName);
  };

  const getIdFromName = (name: string) => {
    const appName =  subscriptions.list.find((subscription) => {
      return name === subscription.applicationInfo?.name;
    })?.applicationInfo?.applicationId;
    // handles keys generated but not associated with an application scenario
    if (appName === undefined) {
      return subscriptions.list[0].applicationInfo?.applicationId;
    }
    return appName;
  };

  const goToCredentialsPage = () => {
    trackTryOutGoToCredentialsClick(`${api?.id}`)
    if (isSandboxKeyGenerationAccessible(allowedScopes)) {
      history.push(
        PATH_SEPARATOR +
        selectedOrgHandleName +
        '/apis/' +
        api.id +
        '/credentials/sandbox'
      );
    } else if (isProductionKeyGenerationAccessible(allowedScopes)) {
      history.push(
        PATH_SEPARATOR +
        selectedOrgHandleName +
        '/apis/' +
        api.id +
        '/credentials/production'
      );
    }
  };

  const onEndpointSelect = (event: ChangeEvent<any>) => {
    const { target: { value } } = event;
    const selectedEnvObject = endpointUrlList.find((endpoint) => {
      return endpoint?.url === value;
    });
    setSelectedEnvironment(selectedEnvObject?.choreoEnvironmentName || "");
    setSelectedGatewayEnvironment(selectedEnvObject?.environment || "");
    if (selectedEndpoint !== value) {
      setToken("");
      setSelectedEndpoint(value);
    }
    setSelectedVisibility(
      getVisibilityFromAccessibilityType(
        selectedEnvObject?.accessibilityType || ""
      )
    );
    // production key-type is selected if the selected endpoint is critical and not a sandbox endpoint
    if (selectedEnvObject?.isCritical) {
      setCurrentKeyType(selectedEnvObject?.isSandbox ? KeyType.SANDBOX : KeyType.PRODUCTION);
      setIsCritivalEnv(true);
    } else {
      setCurrentKeyType(KeyType.SANDBOX);
      setIsCritivalEnv(false);
    }
    setToken("");
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText(selectedEndpoint);
    setCopiedTooltipVisible(true);
    setTimeout(() => {
      setCopiedTooltipVisible(false);
    }, 2000);
  };

  const getKeyGenerationPermissionError = () => {
    if (currentKeyType === KeyType.SANDBOX && !isSandboxKeyGenerationAccessible(allowedScopes)) {
      return intl.formatMessage({
        id: 'api.apiTryOut.buttons.getTestKey.sandbox.disabled.tooltip',
        defaultMessage: 'You do not have permission to generate a sandbox key'
      });
    }
    if (currentKeyType === KeyType.PRODUCTION && !isProductionKeyGenerationAccessible(allowedScopes)) {
      return intl.formatMessage({
        id: 'api.apiTryOut.buttons.getTestKey.production.disabled.tooltip',
        defaultMessage: 'You do not have permission to generate a production key'
      });
    }
    return "";
  }

  const getTestKeyDisabledTooltip = () => {
    const keyGenPermissionError = getKeyGenerationPermissionError();
    if (keyGenPermissionError) {
      return keyGenPermissionError;
    }
    const envKey =
      keysInfo?.keys &&
      keysInfo.keys.list.find((key) => key.keyType === currentKeyType);
    if (
      selectedApplicationId !== "" &&
      Boolean(
        envKey && !envKey.supportedGrantTypes?.includes("client_credentials")
      )
    ) {
      return intl.formatMessage({
        id: 'api.apiTryOut.buttons.getTestKey.tooltip',
        defaultMessage: 'Client credentials grant type is disabled for the selected application'
      });
    }
    return ""
  }

  const getTestKeyGenerationPermissionError = () => {
    if (currentKeyType === KeyType.SANDBOX && !isSandboxKeyGenerationAccessible(allowedScopes)) {
      return intl.formatMessage({
        id: 'api.apiTryOut.buttons.getTestKey.sandbox.disabled.tooltip',
        defaultMessage: 'You do not have permission to generate a sandbox key'
      });
    }
    if (currentKeyType === KeyType.PRODUCTION && !isProductionKeyGenerationAccessible(allowedScopes)) {
      return intl.formatMessage({
        id: 'api.apiTryOut.buttons.getTestKey.production.disabled.tooltip',
        defaultMessage: 'You do not have permission to generate a production key'
      });
    }
    return "";
  }

  const getAccessTokenButtonDisabledStatus = () => {
    if(keysInfo?.currentKey?.mode === 'MAPPED') {
      return true;
    }
    const disabled = !userInfo.apimTokenObtained ||
      (subscriptions && subscriptions.count === 0) ||
      isKeysFetching ||
      (!isDefaultCredentialsAvailable && selectedApplicationId === '') ||
      (isDefaultCredentialsAvailable && subscriptions.count > 1 && selectedApplicationId === '')
      || currentKeyType === KeyType.SANDBOX && !isSandboxKeyGenerationAccessible(allowedScopes)
      || currentKeyType === KeyType.PRODUCTION && !isProductionKeyGenerationAccessible(allowedScopes);
    if (disabled) {
      return true;
    }
    const envKey =
      keysInfo?.keys &&
      keysInfo.keys.list.find((key) => key.keyType === currentKeyType);
    return Boolean(
      envKey && !envKey.supportedGrantTypes?.includes("client_credentials")
    );
  };

  const getTestKeyButtonDisabledStatus = () => {
    if (currentKeyType === KeyType.SANDBOX && !isSandboxKeyGenerationAccessible(allowedScopes)) {
      return true;
    }
    if (currentKeyType === KeyType.PRODUCTION && !isProductionKeyGenerationAccessible(allowedScopes)) {
      return true;
    }
    return false;
  }
  const EnvironmentSelector = () => {
    return enableCustomDomainView ? (
      <Box display='block' justifyContent='left' alignItems='flex-start'>
        <Box
          display='flex'
          flexDirection='row'
          my={2}
          width={0.8}
          alignItems="center"
        >
          <Box
            width={0.3}
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
          >
            <Typography className={classes.label}>
              {endpointUrlList.length > 1 ?
                <FormattedMessage
                id='components.api.ApiTryOut.label.endpoints'
                defaultMessage='Endpoint(s)'
                /> : <FormattedMessage
                  id='components.api.ApiTryOut.label.endpoint'
                  defaultMessage='Endpoint'
                />}
              </Typography>
          </Box>
          <Box
            width={0.7}
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
          >
            <Select
              variant='outlined'
              value={isThirdPartyApi(api) ? endpointUrl : selectedEndpoint}
              onChange={onEndpointSelect}
              className={classes.selectBox}
              MenuProps={{
                getContentAnchorEl: null,
                classes: { paper: classes.dropdownStyle },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }}
              placeholder={intl.formatMessage({
                id: 'components.api.ApiTryOut.select.endpoint.placeholder',
                defaultMessage: 'Select an Endpoint',
              })}
              disabled={isKeysFetching || endpointUrlList.length === 1}
              data-testid='endpoint-selector'
              data-cyid='endpoint-selector'
              endAdornment={
                <DarkTooltip
                  title={
                    <FormattedMessage
                      id='components.api.ApiTryOut.tooltip.copied'
                      defaultMessage='Copied'
                    />
                  }
                  TransitionComponent={Zoom}
                  placement='right'
                  open={copiedTooltipVisible}
                >
                  <IconButton
                    disableRipple
                    onClick={handleCopyClick}
                    className={classes.copyIconButton}
                  >
                    <CustomIcons icon={'copy-icon'} />
                  </IconButton>
                </DarkTooltip>
              }
              renderValue={(value: any) =>
                <Box className={classes.envBox}>
                  {selectedEnvironment && (
                    <Box className={classes.envNameStyles} data-cyid={`endpoint-environment-${selectedEnvironment}`}>
                      {selectedEnvironment} :
                    </Box>
                  )}
                  {selectedVisibility && (
                    <Box
                      className={classes.envChipSecondary}
                      data-cyid={`endpoint-environment-${selectedEnvironment}`}
                    >
                      {selectedVisibility}
                    </Box>
                  )}
                  {currentKeyType === KeyType.SANDBOX && isCritivalEnv && (
                    <Box
                      className={classes.envChipSecondary}
                      data-cyid={`endpoint-environment-${selectedEnvironment}`}
                    >
                      Sandbox
                    </Box>
                  )}
                  <Box className={classes.envValue}>{value}</Box>
                </Box>
              }
            >
              {endpointUrlList?.map((endpoint: EndpointInfo) => {
                return (
                  <MenuItem
                    key={endpoint.environment}
                    value={endpoint.url}
                    data-cyid={`endpoint-list-item-${endpoint.environmentDisplayName}`}
                  >
                    <Box className={classes.envBox}>
                      <Box className={classes.envNameStyles}>
                        {endpoint.choreoEnvironmentName} :
                      </Box>
                      {endpoint.accessibilityType && (
                        <Box className={classes.envChipSecondary}>
                          {getVisibilityFromAccessibilityType(
                            endpoint.accessibilityType
                          )}
                        </Box>
                      )}
                      {endpoint.isSandbox && (
                        <Box className={classes.envChipSecondary}>{"Sandbox"}</Box>
                      )}
                      <Box className={classes.envValue}>{endpoint.url}</Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  const ScopeSelection = () => {
    if (scopes.length === 0 || !Settings.apiTryoutTestKeyEnabled || testTokenType !== TokenType.APPLICATION) {
      return null;
    }

    return (
      <Box display='flex'
        flexDirection='row'
        width={0.8}
        my={1}>
        <Box
          width={0.3}
          display='flex'
          flexDirection='row'
          alignItems='flex-start'
          marginTop='inherit'
        >
          <Typography className={classes.label}>
            <FormattedMessage
              id='components.api.ApiTryOut.label.request.permission'
              defaultMessage='Request Permissions (Scopes)'
            />
          </Typography>
        </Box>
        <Box
          width={0.7}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
        >
          <Box
            width={1}
            display='flex'
            alignSelf={'center'}
            flexDirection='row'
            alignItems='center'
          >
            <CustomAutoComplete
              placeholder={
                intl.formatMessage({
                  id: 'components.shared.AccessTokenDialog.tokenGeneration.labels.select.scopes',
                  defaultMessage: 'Select Scopes to Add to Token',
                })
              }
              options={scopes || []}
              onChange={(_, permissions) => {
                if (selectedScopes) {
                  setPermissionsCallback(permissions);
                }
              }}
              textFieldStyle={classes.textField}
              tagStyle={classes.tag}
              defaultValues={selectedScopes}
              data-testid="dialog-token-generate-scope-selector"
              containerStyle={classes.autoComplete}
            />
          </Box>
        </Box>
      </Box>
    )
  };

  const setPermissionsCallback = (permissions: string[]) => {
    setSelectedScopes(permissions);
  }

  const AccessToken = () => {
    return (
      <Box display='flex'
        flexDirection='row'
        width={0.8}
        my={1}>
        <Box
          width={0.3}
          display='flex'
          flexDirection='row'
          alignItems='flex-start'
          marginTop='inherit'
        >
          <Typography className={classes.label}>Access Token</Typography>
        </Box>
        <Box
          width={0.7}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
        >
          { Settings.apiTryoutTestKeyEnabled && keysInfo?.keys?.list?.filter(key => key.keyType === currentKeyType).length === 0 && (
            <Box display='flex' flexDirection='row' alignItems='center' marginBottom='8px'>
              <Info color='error' fontSize='small' />
              <Box marginLeft='4px'>
                <Typography variant='body2' color='error'>
                  <FormattedMessage
                    id='api.apiTryOut.messages.noOAuthCredentials'
                    defaultMessage="You don't have any OAuth credentials configured for the selected application."
                  />
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            width={1}
            display='flex'
            alignSelf={'center'}
            flexDirection='row'
            alignItems='center'
          >
            <PasswordCopyField
              className={classes.tokenField}
              name='accessToken'
              data-testid='accessToken'
              value={token}
              onChange={handleChanges}
              id='accessTokenInput'
            />
            { getAccessTokenButtonDisabledStatus() && (
              <Tooltip
                title={getKeyGenerationPermissionError() || intl.formatMessage({
                  id: 'api.apiTryOut.labels.accessToken.tooltip',
                  defaultMessage: 'Paste a user token obtained from a subscribed application within your identity provider.',
                })}
                placement='bottom'
                interactive={true}
                data-testid='application-selection-helpertext'
              >
                <HelpTwoTone className={classes.helpIcon} />
              </Tooltip>
            )}
            { !getAccessTokenButtonDisabledStatus() && (
              <span>
                <OutlineButton
                  buttonLabel={intl.formatMessage({
                    id: 'api.apiTryOut.buttons.getAccessToken',
                    defaultMessage: 'Get Access Token',
                  })}
                  className={classes.generateKeyButton}
                  onClick={() => fetchAccessToken(selectedApplicationId, selectedScopes)}
                  startIcon={isUpdating ? <CircularProgress size={15} /> : undefined}
                  testId={'get-test-key-btn'}
                  disabled={Settings.apiTryoutTestKeyEnabled && keysInfo?.keys?.list?.filter(key => key.keyType === currentKeyType).length === 0}
                />
              </span>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const ApiKey = () => {
    return (
      <Box display='flex'
        flexDirection='row'
        width={0.8}
        my={1}>
        <Box
          width={0.3}
          display='flex'
          flexDirection='row'
          alignItems='flex-start'
          marginTop='inherit'
        >
          <Typography className={classes.label}>API Key</Typography>
        </Box>
        <Box
          width={0.7}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
        >
          <Box
            width={1}
            display='flex'
            alignSelf={'center'}
            flexDirection='row'
            alignItems='center'
          >
            <PasswordCopyField
              className={classes.tokenField}
              name='accessToken'
              data-testid='accessToken'
              value={token}
              onChange={handleChanges}
              id='accessTokenInput'
            />
              <Tooltip
                title={getKeyGenerationPermissionError() || intl.formatMessage({
                  id: 'api.apiTryOut.label.apiKey.tooltip',
                  defaultMessage: 'Paste a API Key obtained from the API.',
                })}
                placement='bottom'
                interactive={true}
                data-testid='application-selection-helpertext'
              >
                <HelpTwoTone className={classes.helpIcon} />
              </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  }

  const SubscriptionSelectionPanel = () => {
    if (isThirdPartyApi(api)) {
      return (
        <Box className={classes.securityHeaderButtonBox}>
          {!openSecurityHeader && (
            <Grid container spacing={4}>
              <Grid item />
              <Grid item xs={6} sm={7} className={classes.SecurityHeaderGridSpace}>
                <Button
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setOpenSecurityHeader(true)}
                    className={classes.securityHeaderButton}
                  >
                    <FormattedMessage
                      id="modules.testComponent.ThordPartyApiEndpointConfig.secuirtyHeader.button.label"
                      defaultMessage="Add Security Header"
                    />
                </Button>
              </Grid>
            </Grid>
          )}
          {openSecurityHeader && (
            <SecurityHeader 
              headerName={headerName} 
              headerValue={headerValue} 
              setOpenSecurityHeader={setOpenSecurityHeader}
            />
          )}
        </Box>
      );
    } else if (subscriptions.count === 0 && !Settings.apiTryoutTestKeyEnabled) {
      return (
        <Box display='block' justifyContent='left' alignItems='flex-start'>
          <Grid xs={12} md={8} item>
            <Box display='flex' justifyContent='center' flexDirection='Column'>
              <Box
                flexDirection={'row'}
                alignItems={'space-between'}
                display={'flex'}
                my={2}
              >
                <Warning className={classes.warningIcon} />
                <Typography
                  data-testid='warning-msg'
                  className={classes.warningText}
                >
                  <FormattedMessage
                    id='api.apiTryOut.messages.generateCredentials'
                    defaultMessage='Generate credentials to invoke the API.'
                  />
                </Typography>
                <InfoTooltip
                  content={intl.formatMessage({
                    id: 'api.menuItems.credentials.permission.denied.tooltip',
                    defaultMessage: "You don't have permission to view or generate credentials"
                  })}
                  darken={true}
                  isDisabled={isCredentialMenuAccessible(allowedScopes)}
                >
                  <span>
                    <OutlineButton
                      onClick={goToCredentialsPage}
                      buttonLabel={intl.formatMessage({
                        id: 'api.apiTryOut.buttons.goToCredentials',
                        defaultMessage: 'Go to credentials',
                      })}
                      className={classes.goToCredentialsBtn}
                      startIcon={(
                        <CustomIcons
                          icon='try-button-icon'
                          disabled={!isCredentialMenuAccessible(allowedScopes)}
                        />
                      )}
                      disabled={!isCredentialMenuAccessible(allowedScopes)}
                    />
                  </span>
                </InfoTooltip>
              </Box>
            </Box>
          </Grid>
        </Box>
      );
    } else if (subscriptions.count === 0 && Settings.apiTryoutTestKeyEnabled) {
      return null;
    } else if (isDefaultCredentialsAvailable && subscriptions.count === 1) {
      setSelectedApplicationId(
        subscriptions.list[0].applicationInfo?.applicationId as string
      );
      return null;
    } else if (subscriptions.count > 0 && testTokenType === TokenType.APPLICATION) {
      return (
        <Box
          display="block"
          justifyContent="left"
          alignItems="flex-start"
          mt={2}
        >
          <SubscriptionList />
        </Box>
      );
    } else {
      return null;
    }
  };

  const SubscriptionList = () => {
    return (
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        width={0.8}
      >
        <Box
          width={0.3}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
        >
          <Typography className={classes.textLabel}>
            <FormattedMessage
              id='api.apiTryOut.labels.subscribedApps'
              defaultMessage='Subscribed Application'
            />
          </Typography>
        </Box>
        <Box
          width={0.7}
          display='flex'
          flexDirection='row'
          alignItems='flex-start'
          data-testid='application-selector-wrapper'
        >
          <Select
            variant='outlined'
            value={selectedApplicationName}
            onChange={onTryoutApplicationChange}
            className={classes.selectBox}
            MenuProps={{
              getContentAnchorEl: null,
              classes: { paper: classes.dropdownStyle },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
            placeholder={intl.formatMessage({
              id: 'api.apiTryOut.applications.labels.selectApp',
              defaultMessage: 'Select an Application',
            })}
            disabled={isKeysFetching || !!getKeyGenerationPermissionError()}
            data-testid='application-selector'
          >
            {subscriptions.list.map((subscription) => {
              if (subscription.applicationInfo?.name === defaultAppName) {
                return (
                  <MenuItem
                    key={subscription.applicationInfo?.name}
                    value='Default Credentials'
                  >
                    <FormattedMessage
                      id='api.apiTryOut.subscriptions.defaultCredentials'
                      defaultMessage='Default Credentials'
                    />
                  </MenuItem>
                );
              } else {
                return (
                  <MenuItem
                    key={subscription.applicationInfo?.name}
                    value={subscription.applicationInfo?.name}
                  >
                    {subscription.applicationInfo?.name}
                  </MenuItem>
                );
              }
            })}
          </Select>
          <Tooltip
            title={getKeyGenerationPermissionError() || intl.formatMessage({
              id: 'api.apiTryOut.labels.subscribed.applications.tooltip',
              defaultMessage: 'Applications that are subscribed to the API',
            })}
            placement='bottom'
            interactive={true}
            data-testid='application-selection-helpertext'
          >
            <HelpTwoTone className={classes.helpIcon} />
          </Tooltip>
          <Box ml={2} mt={1.1}>
            {isKeysFetching && <CircularProgress size={18} />}
          </Box>
        </Box>
      </Box>
    );
  }

  const TokenInput = () => {
    if (subscriptions.count === 0 && !Settings.apiTryoutTestKeyEnabled) {
      return null;
    }
    if (subscriptions.count !== 0 && !Settings.apiTryoutTestKeyEnabled) {
      return <AccessToken />;
    }
    return (
      <>
        { testTokenType === TokenType.TEST_KEY && <TestKey /> }
        { testTokenType === TokenType.APPLICATION && <AccessToken /> }
        { testTokenType === TokenType.API_KEY && <ApiKey /> }
      </>
    )
  }

  const PermissionViewer = () => {
    return scopes.length > 0 && keysInfo?.oauthToken && keysInfo.oauthToken.tokenScopes.length > 0 && !!token ? (
      <Box
        display="flex"
        justifyContent="left"
        alignItems="flex-start"
        mb={2}
        width={0.8}
        mt={2}
      >
          <Box className={classes.permissionLabel}>
            <Typography className={classes.label} data-testid="permission-list">
              <FormattedMessage
                id='components.api.ApiTryOut.label.response.permission'
                defaultMessage='Response Permissions (Scopes)'
              />
            </Typography>
          </Box>
          <Box
            className={classes.permissionContent}
            display='flex'
            flexDirection='row'
            alignContent='flex-start'
            flexWrap='wrap'
          >
            { keysInfo.oauthToken.tokenScopes.map(scope => 
              <Box key={scope} className={classes.responseScopeBox}>
                <Badge
                text={scope}
                variant={BadgeVariant.LIGHT}
                outLined
                size={BadgeSize.SMALL}
                key={scope}
                testId={`permission-badge-${getScopeName(scope)}`}
              />
              </Box>
            )}
          </Box>
        </Box>
    ): null
  };

  const handleTokenTypeRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setToken('');
    if (event.target.value === TokenType.TEST_KEY) {
      setTestTokenType(TokenType.TEST_KEY);
    } else if (event.target.value === TokenType.APPLICATION) {
      if (applicationList.length > 0) {
        const firstApp = applicationList[0] as string;
        setSelectedApplicationName(firstApp);
        setSelectedApplicationId(getIdFromName(firstApp) as string);
      }
      setTestTokenType(TokenType.APPLICATION);
    } else if (event.target.value === TokenType.API_KEY) {
      setTestTokenType(TokenType.API_KEY);
      setSecuritySchemeType(SecuritySchemeType.API_KEY);
    }
  };

  useEffect(() => {
    setAuthorizationHeader(getAuthorizationHeader());
  }, [testTokenType]);

  const TokenTypeSelector = () => {
    return (
      <Box display='block' justifyContent='left' alignItems='flex-start'>
        <Box
          display='flex'
          flexDirection='row'
          width={0.8}
          alignItems='center'
        >
          <Box
            width={0.3}
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
          >
            <Typography className={classes.label}>
                <FormattedMessage
                id='components.api.ApiTryOut.label.token.type'
                defaultMessage='Token Type'
                /> 
              </Typography>
          </Box>
          <Box width={0.7} flexDirection="row" alignItems="flex-start">
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="test-key"
              name="radio-buttons-group"
              onChange={handleTokenTypeRadioChange}
              value={testTokenType}
            >
              <Box display="flex" flexDirection="row" alignItems="flex-start">
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="flex-start"
                  mr={2}
                >
                  <Radio value={TokenType.TEST_KEY} id='test-key-radio-btn' />
                  <Typography className={classes.label}>
                    <FormattedMessage
                      id='components.api.TokenType.label.apiKey'
                      defaultMessage='Test Key'
                    />
                  </Typography>
                </Box>
                {isOAuthSupportedApi && subscriptions.count > 0 && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="flex-start"
                    mr={2}
                  >
                    <Radio value={TokenType.APPLICATION} id='application-key-radio-btn' />
                    <Typography className={classes.label}>
                      <FormattedMessage
                        id='components.api.TokenType.label.application.token'
                        defaultMessage='Application Token'
                      />
                    </Typography>
                </Box>
                )}
                {isApiKeySupportedApi && (
                  <Box display='flex' flexDirection='row' alignItems='flex-start' mr={2}>
                    <Radio value={TokenType.API_KEY} id='api-key-radio-btn' />
                    <Typography className={classes.label}>
                      <FormattedMessage
                        id='components.api.TokenType.label.apiKey.auth.header'
                        defaultMessage='API Key'
                      />
                    </Typography>
                  </Box>
                )}
              </Box>
            </RadioGroup>
          </Box>
        </Box>
      </Box>
    );
  };


  const getDeployedApiId = (): string => {
    const matched = api?.versionInfo?.majorVersionEndpointUrls?.find(
      (ep) => ep.choreoEnvironmentName === selectedEnvironment
    );
    return matched?.deployedApiId || api?.id as string;
  };

  const generateTestKey = async () => {
    setIsUpdating(true);
    try {
      const targetApiId = getDeployedApiId();
      await dispatchGenerateApiTestKey(targetApiId, currentKeyType);
      setIsUpdating(false);
    } catch (error) {
      setIsUpdating(false);
      dispatch(
        setErrorNotification(
          'ERROR: ' + error.response.data.description ||
            error.response.data.error_description
        )
      );
    }
  };

  const handleTokenGet = () => {
    testTokenType === TokenType.TEST_KEY ? generateTestKey() : generateAccessToken(selectedApplicationId as string, selectedScopes)
  }

  const TestKey = () => {
    return (
      <Box display='flex'
        flexDirection='row'
        width={0.8}
        my={1}>
        <Box
          width={0.3}
          display='flex'
          flexDirection='row'
          alignItems='flex-start'
          marginTop='inherit'
        >
          <Typography className={classes.label}>
            <FormattedMessage
              id='components.api.APIKey.label.secuirtyHeader'
              defaultMessage='Test Key'
            />
          </Typography>
        </Box>
        <Box
          width={0.7}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
        >
          <Box
            width={1}
            display='flex'
            alignSelf={'center'}
            flexDirection='row'
            alignItems='center'
          >
            <PasswordCopyField
              className={classes.tokenField}
              id="accessTokenInput"
              name="accessToken"
              data-testid="accessToken"
              value={token}
            />
            { getTestKeyButtonDisabledStatus() && (
              <Tooltip
                title={getTestKeyGenerationPermissionError()}
                placement='bottom'
                interactive={true}
                data-testid='application-selection-helpertext'
              >
                <HelpTwoTone className={classes.helpIcon} />
              </Tooltip>
            )}
            { !getTestKeyButtonDisabledStatus() && (
              <span>
                <OutlineButton
                  buttonLabel={intl.formatMessage({
                    id: 'api.apiTryOut.buttons.getTestKey',
                    defaultMessage: 'Get Test Key',
                  })}
                  className={classes.generateKeyButton}
                  onClick={() => handleTokenGet()}
                  startIcon={isUpdating ? <CircularProgress size={15} /> : undefined}
                  testId={'get-test-key-btn'}
                />
              </span>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const TryOutContent = () => {
    if (swaggerLoading || isSubscriptionFetching) {
      return (
        <div className={classes.loaderContainer}>
          <PreLoaderLogo size='medium' />
        </div>
      );
    } else {
      return (
        <Container
          maxWidth='lg'
          className={classes.container}
          classes={{ root: classes.componentPadding }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              {isApimTokenObtained && (
                <Box>
                  <EnvironmentSelector />
                  {Settings.apiTryoutTestKeyEnabled && <TokenTypeSelector />}
                  <div>
                    <SubscriptionSelectionPanel />
                    <ScopeSelection />
                    <TokenInput />
                    <Box>
                      {getIsInternalAPI(api) &&
                      <WarningBanner
                        message={
                          <Box>
                            <Typography>
                              <FormattedMessage
                                id="components.api.ApiTryOut.index.internal.apis.warning.message"
                                defaultMessage={'Endpoints of an internal API are only accessible within WSO2 Developer Platform.' +
                                ' To invoke the API, generate and copy the curl from below and invoke it via the WSO2 Developer Platform low-code editor. ' +
                                'Alternatively, you can use the connector published for this API'} />
                              </Typography>
                            </Box>}
                        testId='internal-api-warning'
                      />}
                    </Box>
                    <PermissionViewer />
                  </div>
                  <Divider className={classes.divider} />
                </Box>
              )}
              {/* Only show swagger download button if API is not grapghql or websocket */}
              {/* TODO: Enable graphql SDL file/ AsyncAPI download */}
              {!(getAPIType(api) == API_TYPE_GRAPHQL || getAPIType(api) == API_TYPE_WS) && (
                <Grid container>
                  <Grid xs={2} md={8} item />
                  <Grid
                    xs={10}
                    md={4}
                    item
                    container
                    justifyContent='flex-end'
                    className={classes.swaggerDownloadBtnGrid}
                  >
                    <a
                      href={getDownloadLink()}
                      download={fileName}
                      className={classes.downloadLink}
                      onClick={() => {
                        trackTryOutSwaggerDownload(api?.id);
                      }}
                    >
                      <OutlineButton
                        startIcon={<CloudDownloadRounded />}
                        buttonLabel='SWAGGER (/SWAGGER.JSON)'
                        className={classes.swaggerDownloadBtn}
                        onClick={() => {
                          trackTryOutSwaggerDownload(api?.id);
                        }}
                      />
                    </a>
                  </Grid>
                </Grid>
              )}
            </Grid>
            {getTestViewForApi(api)}
          </Grid>
        </Container>
      );
    }
  };

  return (
    <div className={classes.content}>
      <ApiInfoBar />
      {isThirdPartyApi(api) ? (
        <TryOutContent />
      ) : (
        <>
          {api && getEndpointUrls(api)?.length > 0 ? (
            <TryOutContent />
          ) : (
              <div className={classes.warningContainer}>
              <Notification color='warning' icon='warning'>
                <Typography variant='body2'>
                  <FormattedMessage
                    id="components.api.ApiTryOut.index.endpoint.unavailable"
                    defaultMessage={
                      "Endpoint URL(s) are unavailable. " +
                      "Contact the API publisher to set up endpoint URLs and deploy the API."
                    }
                  />
                </Typography>
              </Notification>
              </div>
          )}
        </>
      )}
    </div>
  );
}

const mapDispatchToProps = {
  dispatchGenerateAppKeyAndToken: createAppKeysAndToken,
  dispatchGenerateApplicationToken: generateApplicationToken,
  dispatchFetchSubscriptions: fetchSubscriptions,
  dispatchFetchVersionRangeSubscriptions: fetchAPIVersionRangeSubscriptions,
  dispatchFetchAppKeys: fetchAppKeys,
  dispatchFetchGraphqlResponse: fetchGraphqlResponse,
  dispatchGenerateApiTestKey: generateApiTestKey,
  dispatchFetchIDPs: fetchIDPs,
  dispatchFetchSwagger: fetchSwagger,
  dispatchFetchSwaggerUnauthenticated: fetchSwaggerUnauthenticated,
};

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiInfo: state.apiReducer,
    keysInfo: state.applicationKeysReducer,
    subscriptionInfo: state.subscriptionsReducer,
    userInfo: state.userReducer,
    swaggerInfo: state.swaggerReducer,
    graphqlResponse: state.graphqlReducer,
    idpInfo: state.idpReducer,
    organizationMetadataReducer: state.organizationMetadataReducer,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiTryOut);
